import { useCallback, useState } from "react";

import { useTranslation } from "next-i18next";

import Icon from "@components/Icon";
import { useMessage } from "@components/Message";
import useRecaptcha from "@lib/recaptcha/useRecaptcha";
import { useRouter } from "@lib/router";
import { pushSignUpEvent } from "@modules/gtm";
import { QUERY_PARAMS, ROUTES } from "@utils/constants";

import registerUser from "../services/registerUser";
import { TFieldErrors, TRegisterForm, TRegistrationHookReturns } from "../types";
import { FIELD_NAMES } from "../utils/constants";
import mapRegisterFormToRequest from "../utils/mapRegisterFormToRequest";

const useRegistration = (): TRegistrationHookReturns => {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<TFieldErrors>();
  const { t } = useTranslation();
  const { push } = useRouter();

  const handleRecaptcha = useRecaptcha("register");
  const setMessage = useMessage();

  const handleSubmit = useCallback(
    async (formData: TRegisterForm) => {
      try {
        setIsLoading(true);

        const token = await handleRecaptcha();

        /* Using generic error message */
        if (!token) {
          throw new Error();
        }

        const userData = mapRegisterFormToRequest(formData, token);

        const { isSuccess, errorMessage, fieldErrors } = await registerUser(userData);

        if (!isSuccess) {
          setErrors(fieldErrors);
          throw new Error(errorMessage);
        }
        pushSignUpEvent("email", formData[FIELD_NAMES.EMAIL], formData[FIELD_NAMES.MOBILE_NUMBER]);

        push({ pathname: ROUTES.LOGIN, query: { [QUERY_PARAMS.LOGIN_REGISTER_SUCCESS]: 1 } });
      } catch (err: any) {
        setIsLoading(false);
        setMessage({
          message: err?.message || t("registration-general_error"),
          props: { variant: "error", icon: <Icon icon="warning" size="1.75rem" />, isAnimated: false, canClose: true },
          zone: "register",
        });
      }
    },
    [handleRecaptcha, t, push, setMessage]
  );

  return { handleSubmit, isLoading, errors };
};

export default useRegistration;
